import { baseApi } from '@/shared/api/baseApi';
import { ACCOUNTING_TAGS } from '@/shared/api/tags';
import type {
  CashOrderTemplateDto,
  CreateDocumentPayload,
  DocumentAuthor,
  DocumentCashOrderEntry,
  DocumentDto,
  DocumentListPage,
  DocumentPaymentEntry,
  DocumentPrintModel,
  DocumentSummary,
  DuplicateTemplateDto,
  LifecycleActionPayload,
  LifecycleResult,
  ListDocumentsParams,
  PreviewDiff,
  PreviewDocumentPayload,
  ReturnTemplateDto,
  SaveDocumentPayload,
} from '../model/types';

const documentApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    listDocuments: builder.query<DocumentListPage, ListDocumentsParams>({
      query: (params) => ({ url: 'documents', params: toQueryParams(params) }),
      providesTags: ['DocumentList'],
    }),
    documentSummary: builder.query<DocumentSummary, Omit<ListDocumentsParams, 'cursor'>>({
      query: (params) => ({ url: 'documents/summary', params: toQueryParams(params) }),
      providesTags: ['DocumentSummary'],
    }),
    documentAuthors: builder.query<DocumentAuthor[], ListDocumentsParams['type']>({
      query: (type) => ({ url: 'documents/authors', params: { type } }),
      providesTags: ['DocumentList'],
    }),
    getDocument: builder.query<DocumentDto, string>({
      query: (id) => `documents/${id}`,
      providesTags: (_result, _error, id) => [{ type: 'Document', id }],
    }),
    getDocumentStateVersion: builder.query<{ stateVersion: string }, string>({
      query: (id) => `documents/${id}/state-version`,
    }),
    // Шаблоны считаются от текущего состояния (остаток, «уже возвращено», архив товара), поэтому
    // устаревают вместе с документом: возврат, проведённый минуту назад, уже должен быть учтён.
    duplicateTemplate: builder.query<DuplicateTemplateDto, string>({
      query: (id) => `documents/${id}/duplicate-template`,
      providesTags: (_result, _error, id) => [{ type: 'Document', id }, 'Category', 'CategoryStock'],
    }),
    returnTemplate: builder.query<ReturnTemplateDto, string>({
      query: (id) => `documents/${id}/return-template`,
      providesTags: (_result, _error, id) => [{ type: 'Document', id }, 'Category', 'CategoryStock'],
    }),
    documentPayments: builder.query<DocumentPaymentEntry[], string>({
      query: (id) => `documents/${id}/payments`,
      providesTags: (_result, _error, id) => [{ type: 'Document', id }, 'PaymentList'],
    }),
    cashOrderTemplate: builder.query<CashOrderTemplateDto, string>({
      query: (id) => `documents/${id}/cash-order-template`,
      providesTags: (_result, _error, id) => [{ type: 'Document', id }],
    }),
    documentCashOrders: builder.query<DocumentCashOrderEntry[], string>({
      query: (id) => `documents/${id}/cash-orders`,
      providesTags: (_result, _error, id) => [{ type: 'Document', id }, 'DocumentList'],
    }),
    documentPrintModel: builder.query<DocumentPrintModel, string>({
      query: (id) => `documents/${id}/print`,
      providesTags: (_result, _error, id) => [{ type: 'Document', id }],
    }),
    exportDocumentsXlsx: builder.query<Blob, Omit<ListDocumentsParams, 'cursor'>>({
      query: (params) => ({ url: 'documents/export.xlsx', params: toQueryParams(params), responseHandler: (response: Response) => response.blob() }),
    }),
    previewDocument: builder.mutation<PreviewDiff, { id: string; body: PreviewDocumentPayload }>({
      query: ({ id, body }) => ({ url: `documents/${id}/preview`, method: 'POST', body }),
    }),
    createDocument: builder.mutation<DocumentDto, CreateDocumentPayload>({
      query: (body) => ({ url: 'documents', method: 'POST', body }),
      invalidatesTags: ['DocumentList', 'DocumentSummary'],
    }),
    updateDocument: builder.mutation<DocumentDto, { id: string; rowVersion: number; body: SaveDocumentPayload }>({
      query: ({ id, rowVersion, body }) => ({ url: `documents/${id}`, method: 'PUT', body: { ...body, rowVersion } }),
      invalidatesTags: (_r, _e, { id }) => [{ type: 'Document', id }, 'DocumentList', 'DocumentSummary'],
    }),
    postDocument: builder.mutation<LifecycleResult, { id: string; body: LifecycleActionPayload }>({
      query: ({ id, body }) => ({ url: `documents/${id}/post`, method: 'POST', body }),
      invalidatesTags: ACCOUNTING_TAGS,
    }),
    unpostDocument: builder.mutation<LifecycleResult, { id: string; body: LifecycleActionPayload }>({
      query: ({ id, body }) => ({ url: `documents/${id}/unpost`, method: 'POST', body }),
      invalidatesTags: ACCOUNTING_TAGS,
    }),
    cancelDocument: builder.mutation<LifecycleResult, { id: string; body: LifecycleActionPayload }>({
      query: ({ id, body }) => ({ url: `documents/${id}/cancel`, method: 'POST', body }),
      invalidatesTags: ACCOUNTING_TAGS,
    }),
    returnDocumentToDraft: builder.mutation<LifecycleResult, { id: string; rowVersion: number }>({
      query: ({ id, rowVersion }) => ({ url: `documents/${id}/return-to-draft`, method: 'POST', body: { rowVersion } }),
      invalidatesTags: ACCOUNTING_TAGS,
    }),
    deleteDocument: builder.mutation<LifecycleResult, { id: string; body: LifecycleActionPayload }>({
      query: ({ id, body }) => ({ url: `documents/${id}`, method: 'DELETE', body }),
      invalidatesTags: ACCOUNTING_TAGS,
    }),
    restoreDocument: builder.mutation<LifecycleResult, { id: string; rowVersion: number }>({
      query: ({ id, rowVersion }) => ({ url: `documents/${id}/restore`, method: 'POST', body: { rowVersion } }),
      invalidatesTags: ACCOUNTING_TAGS,
    }),
  }),
});

function toQueryParams(params: object): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null) continue;
    out[key] = Array.isArray(value) ? value.join(',') : String(value);
  }
  return out;
}

export const {
  useListDocumentsQuery,
  useLazyListDocumentsQuery,
  useDocumentSummaryQuery,
  useDocumentAuthorsQuery,
  useGetDocumentQuery,
  useLazyGetDocumentQuery,
  useLazyGetDocumentStateVersionQuery,
  useDuplicateTemplateQuery,
  useLazyDuplicateTemplateQuery,
  useReturnTemplateQuery,
  useDocumentPaymentsQuery,
  useCashOrderTemplateQuery,
  useDocumentCashOrdersQuery,
  useDocumentPrintModelQuery,
  useLazyExportDocumentsXlsxQuery,
  usePreviewDocumentMutation,
  useCreateDocumentMutation,
  useUpdateDocumentMutation,
  usePostDocumentMutation,
  useUnpostDocumentMutation,
  useCancelDocumentMutation,
  useReturnDocumentToDraftMutation,
  useDeleteDocumentMutation,
  useRestoreDocumentMutation,
} = documentApi;

export { documentApi };
